/**
 * صفحهٔ جامع یک کد تولید — مقایسه، عملیات (فرم آماده از نرخ‌نامه)، کنترل اول/نهایی، ضایعات، تاریخچه.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button, Card, Col, Descriptions, Drawer, Empty, Input, InputNumber, Modal, Popconfirm, Row,
  Select, Space, Statistic, Table, Tag, Tooltip, Typography, message,
} from "antd";
import {
  CopyOutlined, DeleteOutlined, EditOutlined, ExclamationCircleFilled, PlusOutlined,
  ReloadOutlined, SaveOutlined,
} from "@ant-design/icons";
import Alert from "../../components/GxAlert";
import GxTabs from "../../components/GxTabs";
import GxTable from "../../components/GxTable";
import HelpPopover from "../../components/HelpPopover";
import OpsGrid from "./OpsGrid";
import { listDrafts } from "../../utils/draft";
import EntriesTable from "./EntriesTable";
import { STAGE_MAP, isSewingLocked } from "../../utils/productionStatus";
import { ratesForOrder } from "../../utils/productName";
import FormulaNumber from "../../components/FormulaNumber";
import { CreatableSelect } from "../../components/EditableTable";
import useColorOptions from "../../hooks/useColorOptions";
import { usePayrollMonthOptions, useClosedPayrollMonths, splitPayrollMonth } from "../../utils/payrollMonth";

import {
  addOrderEntry, addOrderEntriesBulk, addOrderQC, createWasteEvent, deleteEntry, deleteOrderQC, deleteWasteEvent,
  getSewingResetImpact, resetSewing, setManualStage,
  addOrderSize, createPieceRate, getBijackOverview, getEmployees, getOrderEntryGrid, getPieceRates, updateEntry,
  setOrderPayrollMonth,
} from "../../api/client";
import { JalaliDateInput, todayISO, toJalali, fmtNum } from "../../utils/dateUtils";
import { formatMoney, shortOpLabel } from "../../utils/money";
import { useAuth } from "../../context/AuthContext";
import { loadOrder, saveOrder } from "../../utils/layoutStore";

const { Text, Title } = Typography;
const rial = (v) => formatMoney(v, { unit: true });

/** رنگِ کارهای تاریخچه — حذف قرمز، ثبت سبز، بقیه خنثی */
const ACTION_COLOR = {
  create: "green", edit: "blue", delete: "red",
  unlock_sewing: "orange", approve: "purple", rename: "gold",
};
const rialCell = (v) => (
  <span className="gx-money">{formatMoney(v)}</span>
);

let _draftSeq = 0;
const newDraftKey = () => `draft-${++_draftSeq}-${Date.now()}`;

function Diff({ value, pct }) {
  if (value == null) return <Text type="secondary">—</Text>;
  if (value === 0) return <Tag color="green">بی‌اختلاف</Tag>;
  return (
    <Tag color={value < 0 ? "red" : "orange"}>
      {value > 0 ? "+" : "−"}{fmtNum(Math.abs(value))}
      {pct != null && ` (${fmtNum(Math.abs(pct))}٪)`}
    </Tag>
  );
}

export default function BijackDrawer({ code, orderId, open, onClose, onChanged, onOpenSibling }) {
  const monthOpts = usePayrollMonthOptions();      // ماه‌های حقوقِ بسته غیرفعال (v2.152.12)
  const closedMonths = useClosedPayrollMonths();   // v2.158.0: ماهِ فعلیِ بسته ⇒ انتخاب قفل
  // کاربرِ بدونِ مجوزِ مبالغ (کنترل کیفیت / مدیر تولید) هیچ ستون و کارتِ مالی نمی‌بیند.
  const { canViewAmounts } = useAuth();
  const money = canViewAmounts("production");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingMonth, setSavingMonth] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [rates, setRates] = useState([]);
  const [batchRows, setBatchRows] = useState([]);
  // تاریخِ ثبتِ کارکرد خودکار «امروز» است — ماهِ محاسبه از بالای صفحه تعیین می‌شود
  // و تاریخِ دقیقِ کار در محاسبه اثری ندارد، فقط برای دیدنِ «کِی وارد شد» می‌ماند.
  const batchDate = todayISO();
  const [batchSaving, setBatchSaving] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState(null);
  const [editModal, setEditModal] = useState(null); // {orderId, entry}
  const [opsDirty, setOpsDirty] = useState(false);   // برگهٔ عملیاتِ ذخیره‌نشده
  const opsSubmitRef = useRef(null);
  const [leaveAsk, setLeaveAsk] = useState(false);   // پاپ‌آپِ «خارج می‌شوید؟»
  const [leaveSaving, setLeaveSaving] = useState(false);
  // صفحهٔ دوختِ فعال — هر (محصول+مدل+رنگ+سایز) مستقل است
  const [activePageKey, setActivePageKey] = useState(null);
  // تبِ فعالِ صفحهٔ محصول per کاربر یادش می‌ماند (مثلِ نمای ثبت‌ها)
  const [tab, setTabState] = useState(() => loadOrder("pick", "bijack-tab") || "compare");
  const setTab = (k) => { saveOrder("pick", "bijack-tab", k); setTabState(k); };

  const load = useCallback(async () => {
    if (!code) return;
    setLoading(true);
    try {
      const r = await getBijackOverview(code, orderId);
      setData(r.data);
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در بارگذاری اطلاعات کد تولید");
    }
    setLoading(false);
  }, [code, orderId]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return;
    getEmployees().then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setEmployees(list.filter((e) => e.status !== "inactive"));
    }).catch(() => {});
    getPieceRates({ active_only: true })
      .then((r) => setRates((r.data || []).filter((c) => (c.operation_kind || "sewing") !== "cut")))
      .catch(() => {});
  }, [open]);

  const refresh = async () => { await load(); onChanged?.(); };

  // پرسنلِ بدونِ کدِ پرسنلی «(null)» نشان داده می‌شد — کد فقط اگر هست بیاید
  const empOpts = employees.map((e) => ({
    value: e.id,
    label: e.employee_code ? `${e.full_name} (${e.employee_code})` : e.full_name,
  }));
  const orders = data?.orders || [];
  const siblings = data?.siblings || [];
  const orderOpts = orders.map((o) => ({
    value: o.order_id, label: [o.product_type, o.color].filter(Boolean).join(" | "),
  }));
  const activeOrder = orders.find((o) => o.order_id === activeOrderId) || orders[0] || null;

  // کاتالوگِ مشترکِ رنگ + رنگ‌های واقعیِ همین بیجک؛ رنگِ تازه با Enter همان لحظه
  // ثبتِ سراسری می‌شود تا در دراپ‌داونِ بعدی دوباره تعریف نشود.
  const { colorOptions, registerColor } = useColorOptions(
    useMemo(() => [
      ...[...orders, ...siblings].map((o) => o.color),
      ...(data?.rows || []).map((r) => r.color),
    ].filter(Boolean), [orders, siblings, data]));

  const sizeOptsOf = (order) => (order?.sizes || []).map((s) => ({
    value: s.size_label, label: `${s.size_label} (بیجک ${fmtNum(s.total_units)})`, size_id: s.id,
  }));
  const ratesOf = (order) => ratesForOrder(rates, order)
    .map((c) => ({
      value: c.id,
      label: shortOpLabel(c.operation_name, c.stitch_lines),
      rate: c,
    }));

  /** همهٔ عملیاتِ نرخ‌نامه — برای وقتی مرحله‌ای زیرِ محصولِ دیگری ثبت شده. */
  const allRateOps = rates.map((c) => ({
    value: c.id,
    label: shortOpLabel(c.operation_name, c.stitch_lines),
    groupLabel: `${c.product_type} — ${shortOpLabel(c.operation_name, c.stitch_lines)}`,
    rate: c,
  }));

  /** عملیاتِ تازه در نرخ‌نامه + برگرداندنش به‌شکلِ ستونِ گرید. */
  const createOperation = async (payload) => {
    const r = await createPieceRate({ ...payload, operation_kind: "sewing" });
    const cat = {
      id: r.data?.id, product_type: payload.product_type,
      operation_name: payload.operation_name, stitch_lines: payload.stitch_lines,
      rate_per_line: payload.rate_per_line, total_rate: r.data?.total_rate,
    };
    setRates((prev) => [...prev, cat]);   // بدونِ رفت‌وبرگشتِ دوباره به سرور
    message.success("عملیات به نرخ‌نامه افزوده شد");
    return { value: cat.id, label: shortOpLabel(cat.operation_name, cat.stitch_lines), rate: cat };
  };

  // ── صفحه‌های دوخت (سرور per سایز می‌دهد) ──
  const sewingPages = data?.sewing_pages || [];
  const activePage = sewingPages.find((p) => p.key === activePageKey) || sewingPages[0] || null;
  // سایزِ بی‌کارکرد با وجودِ کنترلِ اول قفل نیست (v2.112.4) — همان قاعدهٔ `_assert_can_add` در بک‌اند
  const activePageLocked = isSewingLocked(activePage?.sewing_status) && activePage?.has_work !== false;

  /** ماهِ محاسبهٔ **همین سایز** — هر سایز یک پارتِ مستقلِ کاری است و ممکن است
   *  ماهِ حقوقیِ جدا داشته باشد (XL تیر بسته شد، 2XL رفت مرداد). */
  const payrollMonth = activePage?.payroll_year
    ? `${activePage.payroll_year}-${activePage.payroll_month}` : undefined;

  const savePayrollMonth = async (val) => {
    const { payroll_year: y, payroll_month: m } = splitPayrollMonth(val);
    if (!activePage) {
      message.error("پارتی برای ثبتِ ماهِ محاسبه پیدا نشد");
      return;
    }
    setSavingMonth(true);
    try {
      const r = await setOrderPayrollMonth(activePage.order_id, {
        payroll_year: y, payroll_month: m,
        size_label: activePage.size_label || undefined,   // فقط همین سایز
      });
      message.success(r.data.message);
      await load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "ثبتِ ماهِ محاسبه ناموفق بود");
    }
    setSavingMonth(false);
  };


  // صفحهٔ فعال ⇒ محصولِ فعال؛ با عوض‌شدنِ بیجک اولین صفحه انتخاب می‌شود
  useEffect(() => {
    if (!sewingPages.length) return;
    if (!sewingPages.some((p) => p.key === activePageKey)) {
      setActivePageKey(sewingPages[0].key);
    }
  }, [sewingPages, activePageKey]);
  useEffect(() => {
    if (activePage && activePage.order_id !== activeOrderId) setActiveOrderId(activePage.order_id);
  }, [activePage, activeOrderId]);

  /** آخرین کنترلِ نهاییِ همین صفحه (سایز) — فقط نمایشی: «کِی وارد شد». */
  const lastQc2 = useMemo(() => {
    if (!activePage) return null;
    const order = orders.find((o) => o.order_id === activePage.order_id);
    const mine = (order?.qc2 || []).filter(
      (q) => (q.size_label || null) === (activePage.size_label || null));
    if (!mine.length) return null;
    return mine.map((q) => q.check_date).filter(Boolean).sort().slice(-1)[0] || null;
  }, [activePage, orders]);

  /** ثبتِ گرید — هر سلولِ پرشده همان ProductionEntryِ فعلی را می‌سازد. */
  /* ── ویرایشِ ثبت‌های قبلی در همان گرید ──
     editGrid: ردیف‌ها به شکلِ OpsGrid؛ editOrig: ثبت‌های اصلی per (ردیف، عملیات)
     تا موقعِ ذخیره فقط «تفاوت» اعمال شود، نه پاک‌کردن و ثبتِ از نو. */
  const [editGrid, setEditGrid] = useState(null);
  const [editOrig, setEditOrig] = useState(null);

  const openEntryEdit = async () => {
    try {
      const r = await getOrderEntryGrid(activePage.order_id, activePage.size_label || undefined);
      if (!r.data.rows.length) return message.info("ثبتی برای این سایز نیست");
      const orig = new Map();
      const rows = r.data.rows.map((row, i) => {
        const rowKey = row.row_uid || `old-${activePage.order_id}-${i}`;
        const cells = {};
        Object.entries(row.cells).forEach(([catId, c]) => {
          cells[catId] = c.employee_id;
          orig.set(`${rowKey}:${catId}`, c);
        });
        return { rowKey, quantity: row.quantity, notes: row.notes || "", cells };
      });
      setEditOrig(orig);
      setEditGrid(rows);
      if (r.data.reconstructed) {
        message.info("ثبت‌های قدیمی شناسهٔ ردیف ندارند — چیدمان از روی ترتیبِ ورود بازسازی شد");
      }
    } catch (e) {
      message.error(e?.response?.data?.detail || "بارگذاریِ ثبت‌ها ناموفق بود");
    }
  };

  const closeEntryEdit = () => { setEditGrid(null); setEditOrig(null); };

  const submitGrid = async (cells, meta = {}) => {
    try {
      if (meta.editing && editOrig) {
        // دیفِ امن: فقط ستون‌هایی که الان در گرید دیده می‌شوند قابلِ حذف‌اند
        const visible = new Set((meta.gridCatalogIds || []).map(Number));
        const desired = new Map(cells.map((c) => [`${c.row_uid}:${c.catalog_id}`, c]));
        let updated = 0, added = 0, removed = 0;
        for (const [key, o] of editOrig) {
          const catId = Number(key.split(":")[1]);
          const d = desired.get(key);
          if (!d) {
            if (visible.has(catId)) { await deleteEntry(activePage.order_id, o.entry_id); removed += 1; }
            continue;
          }
          if (d.employee_id !== o.employee_id || d.quantity !== o.quantity) {
            await updateEntry(activePage.order_id, o.entry_id,
              { employee_id: d.employee_id, quantity: d.quantity });
            updated += 1;
          }
          desired.delete(key);
        }
        for (const d of desired.values()) {
          await addOrderEntry(activePage.order_id, {
            employee_id: d.employee_id, catalog_id: d.catalog_id, quantity: d.quantity,
            entry_date: batchDate, size_label: activePage.size_label || null,
            color: activeOrder?.color || null, notes: d.notes, row_uid: d.row_uid,
          });
          added += 1;
        }
        message.success(`ویرایش ذخیره شد — ${fmtNum(updated)} تغییر، `
          + `${fmtNum(added)} جدید، ${fmtNum(removed)} حذف`);
        closeEntryEdit();
        await refresh();
        return;
      }
      /* یک‌جا و تراکنشی (v2.112.4): یا همهٔ برگه ذخیره می‌شود یا هیچ — دیگر نیمی از برگه ثبت و بقیه ۴۰۹ نمی‌شود. */
      await addOrderEntriesBulk(activePage.order_id, cells.map((c) => ({
        employee_id: c.employee_id,
        catalog_id: c.catalog_id,
        quantity: c.quantity,
        entry_date: batchDate,
        size_label: activePage.size_label || null,
        color: activeOrder?.color || null,
        notes: c.notes,
        row_uid: c.row_uid,
      })));
      message.success(`${fmtNum(cells.length)} کارکرد ثبت شد`);
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : (d?.message || "ثبت کارکرد ناموفق بود"));
      throw e;      // تا گرید پیش‌نویس را پاک نکند
    }
  };

  /** بازکردنِ قفل = حذفِ کنترلِ اولِ همین سایز (در تاریخچه ثبت می‌شود). */
  const unlockActivePage = () => {
    const order = orders.find((o) => o.order_id === activePage?.order_id);
    const qcs = (order?.qc1 || []).filter(
      (q) => (q.size_label || null) === (activePage?.size_label || null));
    if (!qcs.length) return message.warning("کنترل اولی برای این سایز پیدا نشد");
    Modal.confirm({
      title: "بازکردنِ قفلِ دوخت؟",
      content: `${fmtNum(qcs.length)} ثبتِ کنترل اولِ «${activePage.label}» حذف می‌شود تا `
        + "عملیات دوباره قابلِ اصلاح شود. کارکردها دست‌نخورده می‌مانند و این کار در تاریخچه ثبت می‌شود.",
      okText: "بله، باز کن", cancelText: "انصراف", okButtonProps: { danger: true },
      onOk: async () => {
        try {
          for (const q of qcs) await deleteOrderQC(activePage.order_id, q.id);
          message.success("قفل باز شد — حالا می‌توانید اصلاح کنید");
          await refresh();
        } catch (e) {
          message.error(e?.response?.data?.detail || "بازکردنِ قفل ناموفق بود");
        }
      },
    });
  };

  /** پاک‌کردنِ کلِ دوخت (v2.116.0): همهٔ کارکرد/کنترل/دفتر ۲ِ این سایز — یا کلِ محصول — صفر می‌شود و به «جدید»
   *  برمی‌گردد تا بشود ردیف را از بیجک حذف کرد (مثلاً کدی که با مدلِ اشتباه دوبار ثبت شده). برش دست نمی‌خورد. */
  const resetSewingDialog = async (wholeProduct) => {
    if (!activePage) return;
    const size = wholeProduct ? null : (activePage.size_label || null);
    let imp;
    try {
      imp = (await getSewingResetImpact(activePage.order_id, size || undefined)).data;
    } catch (e) {
      return message.error(e?.response?.data?.detail || "پیش‌نمایش ناموفق بود");
    }
    const c = imp.counts || {};
    const scopeLabel = wholeProduct ? `کلِ محصول (همهٔ سایزها)` : `سایزِ «${activePage.label}»`;
    if (!(c.entries || c.qc1 || c.qc2 || c.final_entries || c.packaging)) {
      return message.info(`${scopeLabel} دوختی ندارد`);
    }
    if (imp.closed_months?.length) {
      return Modal.error({
        title: "پاک‌کردن ممکن نیست",
        content: `بخشی از این دوخت در حقوقِ بسته‌شدهٔ ${imp.closed_months.join("، ")} پرداخت شده است.`,
      });
    }
    Modal.confirm({
      title: `پاک‌کردنِ کلِ دوختِ ${scopeLabel}؟`,
      width: 480,
      content: (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Text>این‌ها پاک می‌شوند و صفحه به حالتِ «جدید / در حال دوخت» برمی‌گردد:</Text>
          <Text>• {fmtNum(c.entries)} کارکردِ دوخت ({fmtNum(c.workers)} نفر)</Text>
          {!!(c.qc1 || c.qc2) && <Text>• {fmtNum(c.qc1)} کنترل اول و {fmtNum(c.qc2)} کنترل نهایی</Text>}
          {!!c.final_entries && <Text>• {fmtNum(c.final_entries)} ثبتِ دفتر ۲</Text>}
          {!!c.packaging && <Text>• ثبتِ بسته‌بندی</Text>}
          {!!imp.payroll_rows && money && (
            <Text type="danger">• {fmtNum(imp.payroll_rows)} رکوردِ حقوق به مبلغِ {fmtNum(imp.payroll_amount)} ریال از حقوقِ پرسنل کم می‌شود</Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            برش و کارکردِ برشکار دست نمی‌خورند. همهٔ پاک‌شده‌ها با جزئیات در تاریخچه می‌ماند.
          </Text>
        </Space>
      ),
      okText: "بله، کلِ دوخت پاک شود", cancelText: "انصراف", okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const r = await resetSewing(activePage.order_id, size);
          message.success(r.data?.message || "دوخت پاک شد");
          await refresh();
        } catch (e) {
          const d = e?.response?.data?.detail;
          message.error(typeof d === "string" ? d : (d?.message || "پاک‌کردنِ دوخت ناموفق بود"));
        }
      },
    });
  };

  /** سایزِ «بدونِ برش» را به بیجک برمی‌گرداند (تعدادِ جین/تکی از کاربر). */
  const addOrphanSizeToBijack = () => {
    if (!activePage?.orphan) return;
    let jin = 0, extra = 0;
    Modal.confirm({
      title: `افزودنِ سایز «${activePage.size_label}» به بیجک`,
      content: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            تعدادِ برشِ این سایز را وارد کنید (همان چیزی که در بیجک باید می‌بود). دوخت و کنترلِ
            ثبت‌شده دست نمی‌خورند.
          </Text>
          <Space>
            <span>جین</span><InputNumber min={0} defaultValue={0} onChange={(v) => { jin = Number(v) || 0; }} />
            <span>تکی</span><InputNumber min={0} defaultValue={0} onChange={(v) => { extra = Number(v) || 0; }} />
          </Space>
        </Space>
      ),
      okText: "افزودن", cancelText: "انصراف",
      onOk: async () => {
        try {
          const r = await addOrderSize(activePage.order_id, {
            size_label: activePage.size_label, jin_qty: jin, extra_units: extra,
          });
          message.success(r.data.message || "سایز به بیجک افزوده شد");
          await refresh();
        } catch (e) {
          message.error(e?.response?.data?.detail || "افزودنِ سایز ناموفق بود");
        }
      },
    });
  };

  // خلاصه — از rows + QCهای سفارش‌ها محاسبه می‌شود تا بعد از ثبت/ویرایش/حذف همیشه تازه باشد.
  // گاتچا: قبلاً useMemo([data]) بود و هنگامِ سوییچ بینِ محصولاتِ هم‌کد، کارت‌های بالای
  // درایور مقدارِ محصولِ قبلی را نشان می‌دادند (جدولِ مقایسه درست بود ولی کارت‌ها نه).
  // محاسبه ارزان است (چند ردیف)، پس بدونِ memo انجام می‌شود تا همیشه با data هم‌خوان باشد.
  const summary = (() => {
    const rows = data?.rows || [];
    const cut_qty = rows.reduce((a, r) => a + (Number(r.cut_qty) || 0), 0);
    const qc1_qty = rows.reduce((a, r) => a + (Number(r.qc1_qty) || 0), 0);
    const good_qty = rows.reduce((a, r) => a + (Number(r.good_qty) || 0), 0);
    const defect_qty = rows.reduce((a, r) => a + (Number(r.defect_qty) || 0), 0);
    const final_qty = good_qty + defect_qty;
    const waste_qty = rows.reduce((a, r) => a + (Number(r.waste_qty) || 0), 0);
    const sewing_qty = rows.reduce((a, r) => a + (Number(r.sewing_qty) || 0), 0);
    const estimated = (data?.orders || []).reduce(
      (a, o) => a + (Number(o.operations?.estimated_complete_qty) || 0), 0);
    const pct = (diff, base) => (base ? Math.round((diff / base) * 10000) / 100 : null);
    // «ثبت شده یا نه» جدا از «چند است» — کنترلِ صفرِ واقعی هم باید اختلاف نشان دهد
    const qc1Seen = (data?.orders || []).some((o) => (o.qc1 || []).length > 0);
    const finalSeen = (data?.orders || []).some((o) => (o.qc2 || []).length > 0);
    return {
      cut_qty, qc1_qty, good_qty, defect_qty, final_qty, waste_qty, sewing_qty,
      estimated_complete_qty: estimated,
      qc1_diff: qc1Seen ? qc1_qty - cut_qty : null,
      qc1_diff_pct: qc1Seen ? pct(qc1_qty - cut_qty, cut_qty) : null,
      final_diff: finalSeen ? final_qty - cut_qty : null,
      final_diff_pct: finalSeen ? pct(final_qty - cut_qty, cut_qty) : null,
    };
  })();

  /**
   * «برای این پارت از قبل چه چیزی ثبت شده؟»
   *
   * چرا لازم شد: ثبت‌های قبلی **ته** تبِ عملیات‌اند، پایین‌ترِ گریدِ بلندِ ورود.
   * کاربر صفحه را باز می‌کرد، گرید را می‌دید، و بی‌خبر از اینکه کارِ همین پارت
   * قبلاً وارد شده دوباره ثبت می‌کرد. این خلاصه بالای صفحه می‌نشیند تا همان
   * ثانیهٔ اول معلوم باشد.
   */
  const partState = useMemo(() => {
    const order = orders.find((o) => o.order_id === activePage?.order_id);
    const sameSize = (x) => !activePage?.size_label
      || (x.size_label || null) === activePage.size_label;
    const entries = (order?.entries || []).filter(sameSize);
    const qc1 = (order?.qc1 || []).filter(sameSize);
    const qc2 = (order?.qc2 || []).filter(sameSize);
    const waste = (data?.waste_events || []).filter(
      (w) => (!activePage?.order_id || w.order_id === activePage.order_id) && sameSize(w));
    const dates = entries.map((e) => e.entry_date).filter(Boolean).sort();
    return {
      entries: entries.length,
      workers: new Set(entries.map((e) => e.employee_id).filter(Boolean)).size,
      ops: new Set(entries.map((e) => e.catalog_id).filter(Boolean)).size,
      quantity: entries.reduce((a, e) => a + (Number(e.quantity) || 0), 0),
      firstDate: dates[0] || null,
      lastDate: dates[dates.length - 1] || null,
      qc1: qc1.length, qc2: qc2.length, waste: waste.length,
      anything: entries.length + qc1.length + qc2.length + waste.length > 0,
    };
  }, [orders, activePage, data]);

  /* پرشِ نرم به بخشِ ثبت‌های قبلی — دکمهٔ «ببینمشان» باید واقعاً ببرد آنجا،
     وگرنه همان اسکرولِ کورِ قبلی است. */
  const recordedRef = useRef(null);
  const jumpToRecorded = () => {
    setTab("ops");
    setTimeout(() => recordedRef.current?.scrollIntoView(
      { behavior: "smooth", block: "start" }), 120);
  };

  // وقتی محصول فعال عوض شد → ردیف‌های آماده از نرخ‌نامه
  const seedBatchRows = useCallback((order) => {
    if (!order) { setBatchRows([]); return; }
    const ops = ratesForOrder(rates, order);
    const defaultSize = order.sizes?.[0]?.size_label || null;
    setBatchRows(ops.map((op) => ({
      rowKey: `preset-${op.id}`,
      catalog_id: op.id,
      operation_name: op.operation_name,
      stitch_lines: op.stitch_lines,
      rate_per_line: op.rate_per_line,
      total_rate: op.total_rate,
      lines_missing: op.stitch_lines == null || Number(op.stitch_lines) <= 0,
      size_label: defaultSize,
      color: order.color || null,
      employee_id: null,
      quantity: null,
      notes: "",
    })));
  }, [rates]);

  useEffect(() => {
    if (!orders.length) return;
    const id = activeOrderId && orders.some((o) => o.order_id === activeOrderId)
      ? activeOrderId : orders[0].order_id;
    if (id !== activeOrderId) setActiveOrderId(id);
  }, [orders, activeOrderId]);

  const activeOrderKey = activeOrder ? activeOrder.order_id : null;
  //  🐞 قبلاً `activeOrder` (آبجکت) در deps بود و هر بار که `data` دوباره خوانده
  //  می‌شد — تغییرِ ماهِ محاسبه، دکمهٔ تازه‌سازی، هر refresh — هویتش عوض می‌شد و
  //  این افکت ردیف‌های پیش‌نویس را از نو می‌ساخت؛ یعنی پرسنل/تعدادِ تایپ‌شده و
  //  ذخیره‌نشده بی‌صدا پاک می‌شد. حالا فقط با **عوض‌شدنِ واقعیِ محصول** seed می‌شود.
  const seededForRef = useRef(null);
  useEffect(() => {
    if (!activeOrderKey || !rates.length) return;
    if (seededForRef.current === activeOrderKey) return;   // همان محصول ⇒ پیش‌نویس دست‌نخورده
    seededForRef.current = activeOrderKey;
    seedBatchRows(activeOrder);
  }, [activeOrderKey, rates.length]);   // activeOrder عمداً نیست (هر بارگذاری هویتش عوض می‌شود)

  const updateDraft = (rowKey, field, val) => {
    setBatchRows((prev) => prev.map((r) => {
      if (r.rowKey !== rowKey) return r;
      const next = { ...r, [field]: val };
      if (field === "catalog_id" && val) {
        const cat = rates.find((c) => c.id === val);
        if (cat) {
          next.operation_name = cat.operation_name;
          next.stitch_lines = cat.stitch_lines;
          next.rate_per_line = cat.rate_per_line;
          next.total_rate = cat.total_rate;
          next.lines_missing = cat.stitch_lines == null || Number(cat.stitch_lines) <= 0;
        }
      }
      return next;
    }));
  };

  const duplicateDraft = (row) => {
    setBatchRows((prev) => {
      const idx = prev.findIndex((r) => r.rowKey === row.rowKey);
      const copy = {
        ...row,
        rowKey: newDraftKey(),
        employee_id: null,
        quantity: null,
        notes: "",
      };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  const addManualDraft = () => {
    const defaultSize = activeOrder?.sizes?.[0]?.size_label || null;
    setBatchRows((prev) => [...prev, {
      rowKey: newDraftKey(),
      catalog_id: null,
      operation_name: "",
      stitch_lines: null,
      rate_per_line: null,
      total_rate: 0,
      lines_missing: true,
      size_label: defaultSize,
      color: activeOrder?.color || null,
      employee_id: null,
      quantity: null,
      notes: "",
    }]);
  };

  const removeDraft = (rowKey) => {
    setBatchRows((prev) => prev.filter((r) => r.rowKey !== rowKey));
  };

  const submitBatch = async () => {
    if (!activeOrder) return;
    const toSave = batchRows.filter((r) => r.catalog_id && r.employee_id && r.quantity > 0);
    if (!toSave.length) return message.warning("حداقل یک ردیف را با پرسنل و تعداد کامل کنید");
    setBatchSaving(true);
    try {
      for (const r of toSave) {
        await addOrderEntry(activeOrder.order_id, {
          employee_id: r.employee_id,
          catalog_id: r.catalog_id,
          quantity: r.quantity,
          entry_date: batchDate,
          size_label: r.size_label || null,
          color: r.color || activeOrder.color || null,
          notes: r.notes || null,
        });
      }
      message.success(`${toSave.length} عملیات ثبت شد`);
      // ردیف‌های آماده را نگه دار، فقط پرسنل/تعداد را خالی کن (مثل نسخه قبلی)
      setBatchRows((prev) => prev.map((r) => ({ ...r, employee_id: null, quantity: null, notes: "" })));
      await refresh();
    } catch (e) {
      message.error(e?.response?.data?.detail || "ثبت عملیات ناموفق بود");
    }
    setBatchSaving(false);
  };

  const saveEdit = async () => {
    const m = editModal;
    if (!m) return;
    const e = m.entry;
    if (!e.employee_id || !e.catalog_id || !(e.quantity > 0)) {
      return message.warning("پرسنل، عملیات و تعداد الزامی است");
    }
    try {
      await updateEntry(m.orderId, e.id, {
        employee_id: e.employee_id,
        catalog_id: e.catalog_id,
        quantity: e.quantity,
        entry_date: e.entry_date,
        size_label: e.size_label || null,
        notes: e.notes || null,
      });
      message.success("ثبت ویرایش شد");
      setEditModal(null);
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.detail || "ویرایش ناموفق بود");
    }
  };

  // ------------------------------------------------ تب ۱: مقایسه
  const compareCols = [
    { title: "محصول | رنگ | سایز", dataIndex: "display", width: 250, fixed: "right",
      // کلیک ⇒ همان پارت در تبِ عملیات باز می‌شود (بدونِ ترکِ صفحه)
      render: (v, r) => {
        const key = `${r.order_id}:${r.size_label === "—" ? "" : (r.size_label || "")}`;
        const pg = sewingPages.find((p2) => p2.key === key);
        return (
          <a onClick={() => { if (pg) { setActivePageKey(pg.key); setTab("ops"); } }}>
            {pg && (
              <Tag color={STAGE_MAP[pg.sewing_status]?.color} style={{ marginInlineEnd: 4 }}>
                {STAGE_MAP[pg.sewing_status]?.label}
              </Tag>
            )}
            {v}
          </a>
        );
      } },
    { title: "بیجک برش", dataIndex: "cut_qty", width: 100, align: "center",
      render: (v) => <b>{fmtNum(v)}</b> },
    { title: "دوخت", dataIndex: "sewing_qty", width: 90, align: "center", render: fmtNum },
    { title: "کنترل اول", dataIndex: "qc1_qty", width: 100, align: "center", render: fmtNum },
    { title: "اختلاف کنترل اول", width: 140, align: "center",
      render: (_, r) => <Diff value={r.qc1_diff} pct={r.qc1_diff_pct} /> },
    { title: "سالم", dataIndex: "good_qty", width: 90, align: "center", render: fmtNum },
    { title: "زده‌دار", dataIndex: "defect_qty", width: 90, align: "center", render: fmtNum },
    { title: "کنترل نهایی (سالم+زده‌دار)", dataIndex: "final_qty", width: 170, align: "center",
      render: (v) => <b>{fmtNum(v)}</b> },
    { title: "اختلاف کنترل نهایی", width: 150, align: "center",
      render: (_, r) => <Diff value={r.final_diff} pct={r.final_diff_pct} /> },
    { title: "ضایعات", dataIndex: "waste_qty", width: 90, align: "center", render: fmtNum },
  ];

  const compareTab = (
    <Table size="small" rowKey="key" dataSource={data?.rows || []} columns={compareCols}
      pagination={false} scroll={{ x: "max-content" }}
      summary={() => (
        <Table.Summary fixed="bottom">
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}><b>جمع</b></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="center"><b>{fmtNum(summary.cut_qty)}</b></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="center">{fmtNum(summary.sewing_qty)}</Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="center">{fmtNum(summary.qc1_qty)}</Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="center">
              <Diff value={summary.qc1_diff} pct={summary.qc1_diff_pct} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="center">{fmtNum(summary.good_qty)}</Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="center">{fmtNum(summary.defect_qty)}</Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="center"><b>{fmtNum(summary.final_qty)}</b></Table.Summary.Cell>
            <Table.Summary.Cell index={8} align="center">
              <Diff value={summary.final_diff} pct={summary.final_diff_pct} />
            </Table.Summary.Cell>
            <Table.Summary.Cell index={9} align="center">{fmtNum(summary.waste_qty)}</Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )} />
  );

  // ------------------------------------------------ کنترل اول/نهایی (جدولی)
  //
  // مثلِ دفترِ کارخانه: یک ردیف به‌ازای هر سایزِ همان محصول، تعدادِ مبنا کنارِ
  // خانهٔ ورودی، و یک دکمهٔ ثبت برای همهٔ ردیف‌ها. کنترل‌چی و تاریخ یک‌بار بالای
  // جدول انتخاب می‌شوند و برای همهٔ ردیف‌ها به کار می‌روند.
  const [qcChecker, setQcChecker] = useState(null);
  const [qcDate, setQcDate] = useState(todayISO());
  const [qcDrafts, setQcDrafts] = useState({ qc1: {}, qc2: {} });
  const [qcSaving, setQcSaving] = useState(false);

  // محصولِ کنترل همان محصولِ فعالِ صفحه است — دیگر جداگانه انتخاب نمی‌شود.
  const qcOrder = activeOrderId;
  const qcOrderObj = activeOrder;

  useEffect(() => { setQcDrafts({ qc1: {}, qc2: {} }); }, [qcOrder]);

  /** یک ردیف برای هر سایز + تعدادِ مبنا و جمعِ مراحلِ قبل (از rowsِ همین overview) */
  const qcSizeRows = useMemo(() => {
    const rows = (data?.rows || []).filter((r) => r.order_id === qcOrder);
    // فقط سایزِ صفحهٔ فعال — هر سایز کنترلِ مستقلِ خودش را دارد
    const sizes = (qcOrderObj?.sizes || []).filter(
      (z) => !activePage?.size_label || z.size_label === activePage.size_label);
    const list = sizes.length
      ? sizes.map((s) => ({ size_label: s.size_label }))
      : [{ size_label: activePage?.size_label || null }];   // سایزِ بدونِ برش هم ردیفِ خودش
    return list.map((s) => {
      const match = rows.filter((r) => (r.size_label || null) === (s.size_label || null));
      const sum = (k) => match.reduce((a, r) => a + (Number(r[k]) || 0), 0);
      return {
        key: s.size_label || "__nosize__",
        size_label: s.size_label,
        cut_qty: sum("cut_qty"),
        sewing_qty: sum("sewing_qty"),
        qc1_qty: sum("qc1_qty"),
        good_qty: sum("good_qty"),
        defect_qty: sum("defect_qty"),
      };
    });
  }, [data, qcOrder, qcOrderObj, activePage]);

  const setQcDraft = (stage, key, field, val) => setQcDrafts((prev) => ({
    ...prev,
    [stage]: { ...prev[stage], [key]: { ...(prev[stage][key] || {}), [field]: val } },
  }));

  const qcFilled = (stage) => qcSizeRows
    .map((r) => ({ row: r, d: qcDrafts[stage][r.key] || {} }))
    .filter(({ d }) => (stage === "qc1"
      ? d.passed != null
      : (d.passed != null || d.rejected != null)));

  const submitQCBatch = async (stage) => {
    if (!qcOrder) { message.warning("محصولی برای ثبت نیست"); return false; }
    const toSave = qcFilled(stage);
    if (!toSave.length) { message.warning("حداقل تعدادِ یک سایز را وارد کنید"); return false; }
    /* 🐞 v2.109.0 — ریشهٔ گزارشِ کدهای ۶۳/۶۸: کنترلِ اول پیش از ثبتِ کارکردِ دوخت زده شد،
       سایز قفل شد و کارکرد هیچ‌وقت ثبت نشد (بعد QC را برای بازکردن پاک کردند). حالا اول می‌پرسیم. */
    // v2.118.0 (کاربر ۱۴۰۵/۰۷/۰۴): کنترل اول فقط بعد از ثبتِ دوختِ همان سایز — دیگر «باز هم ثبت شود؟» ندارد.
    const noWork = stage === "qc1" ? toSave.filter(({ row }) => !Number(row.sewing_qty)) : [];
    if (noWork.length) {
      Modal.warning({
        title: "اول دوخت را ثبت کنید",
        content: `سایز ${noWork.map(({ row }) => row.size_label || "بدون سایز").join("، ")} هنوز هیچ کارکردِ دوختی ندارد. `
          + "کنترل اول بعد از ثبتِ دوخت (بالای همین صفحه) ممکن است.",
      });
      return false;
    }
    setQcSaving(true);
    let ok = true;
    try {
      for (const { row, d } of toSave) {
        await addOrderQC(qcOrder, {
          stage,
          size_label: row.size_label || null,
          checker_employee_id: qcChecker || null,
          check_date: qcDate,
          passed_qty: d.passed || 0,
          rejected_qty: stage === "qc2" ? (d.rejected || 0) : 0,
          notes: d.notes || null,
        });
      }
      message.success(`${fmtNum(toSave.length)} ردیف ${stage === "qc1" ? "کنترل اول" : "کنترل نهایی"} ثبت شد`);
      setQcDrafts((prev) => ({ ...prev, [stage]: {} }));
      await refresh();
    } catch (e) {
      message.error(e?.response?.data?.detail || "ثبت کنترل ناموفق بود");
      ok = false;
    }
    setQcSaving(false);
    return ok;
  };

  const qcDraftCols = (stage) => {
    const num = (field, title, width = 110) => ({
      title, width, align: "center",
      render: (_, r) => {
        const noSew = stage === "qc1" && !Number(r.sewing_qty);
        return (
          <Tooltip title={noSew ? "اول دوختِ این سایز را ثبت کنید" : ""}>
            <span><FormulaNumber min={0} disabled={noSew}
              placeholder={noSew ? "بعد از ثبتِ دوخت" : undefined}
              value={qcDrafts[stage][r.key]?.[field] ?? null}
              onChange={(v) => setQcDraft(stage, r.key, field, v)} /></span>
          </Tooltip>
        );
      },
    });
    return [
      { title: "سایز", dataIndex: "size_label", width: 90, fixed: "right",
        render: (v) => <b>{v || "—"}</b> },
      { title: "تعداد بیجک", dataIndex: "cut_qty", width: 100, align: "center",
        render: (v) => <b>{fmtNum(v)}</b> },
      ...(stage === "qc1"
        ? [
            { title: "دوخت", dataIndex: "sewing_qty", width: 85, align: "center", render: fmtNum },
            { title: "کنترل اولِ ثبت‌شده", dataIndex: "qc1_qty", width: 140, align: "center",
              render: (v) => fmtNum(v) },
            num("passed", "تعداد کنترل اول"),
          ]
        : [
            { title: "جمع کنترل اول", dataIndex: "qc1_qty", width: 120, align: "center", render: fmtNum },
            { title: "سالمِ ثبت‌شده", dataIndex: "good_qty", width: 110, align: "center", render: fmtNum },
            { title: "زده‌دارِ ثبت‌شده", dataIndex: "defect_qty", width: 120, align: "center", render: fmtNum },
            num("passed", "سالم", 100),
            num("rejected", "زده‌دار", 100),
          ]),
      { title: "توضیح", width: 170,
        render: (_, r) => (
          <Input value={qcDrafts[stage][r.key]?.notes || ""}
            onChange={(e) => setQcDraft(stage, r.key, "notes", e.target.value)} />
        ) },
    ];
  };

  /** کارتِ ثبتِ جدولیِ یک مرحلهٔ کنترل — اطلاعاتِ مرحلهٔ قبل فقط نمایش داده می‌شود */
  const qcEntryCard = (stage) => {
    const filled = qcFilled(stage);
    const totals = filled.reduce((a, { d }) => ({
      passed: a.passed + (Number(d.passed) || 0),
      rejected: a.rejected + (Number(d.rejected) || 0),
    }), { passed: 0, rejected: 0 });
    return (
      <Card size="small" style={{ marginBottom: 12 }}
        title={stage === "qc1" ? "ثبت کنترل اول" : "ثبت کنترل نهایی"}
        extra={
          <Space wrap>
            <Select showSearch allowClear value={qcChecker} onChange={setQcChecker}
              options={empOpts} optionFilterProp="label" placeholder="کنترل‌چی"
              style={{ minWidth: 170 }} />
            <JalaliDateInput value={qcDate} onChange={setQcDate} />
          </Space>
        }>
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
          {[
            data?.bijack_no ? `شماره بیجک: ${data.bijack_no}` : null,
            `کد تولید: ${code}`,
            qcOrderObj ? `محصول: ${[qcOrderObj.product_type, qcOrderObj.color].filter(Boolean).join(" | ")}` : null,
          ].filter(Boolean).join("  •  ")}
        </Text>
        <Table size="small" rowKey="key" pagination={false} scroll={{ x: "max-content" }}
          dataSource={qcSizeRows} columns={qcDraftCols(stage)}
          locale={{ emptyText: "سایزی برای این محصول ثبت نشده" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 10, flexWrap: "wrap", gap: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {filled.length
              ? (stage === "qc1"
                  ? <>{fmtNum(filled.length)} ردیف آماده — جمع: <b>{fmtNum(totals.passed)}</b></>
                  : <>{fmtNum(filled.length)} ردیف آماده — سالم <b>{fmtNum(totals.passed)}</b>{" "}
                      + زده‌دار <b>{fmtNum(totals.rejected)}</b> = آمادهٔ ارسال{" "}
                      <b>{fmtNum(totals.passed + totals.rejected)}</b></>)
              : "تعدادِ هر سایز را در ستونِ ورودی بنویسید"}
          </Text>
          <Button type="primary" icon={<SaveOutlined />} loading={qcSaving}
            disabled={!filled.length} onClick={() => submitQCBatch(stage)}>
            {stage === "qc1" ? "ثبت کنترل اول" : "ثبت کنترل نهایی"}
          </Button>
        </div>
      </Card>
    );
  };

  const qcList = (stage) => {
    // فقط کنترل‌های همین پارت و همین سایز — نه همهٔ کد تولید
    const rows = orders
      .filter((o) => o.order_id === activePage?.order_id)
      .flatMap((o) => (stage === "qc1" ? o.qc1 : o.qc2)
        .filter((q) => !activePage?.size_label
          || (q.size_label || null) === activePage.size_label)
        .map((q) => ({
          ...q, product: [o.product_type, o.variant, o.color, q.size_label]
            .filter(Boolean).join(" | "),
        })));
    const cols = [
      { title: "محصول", dataIndex: "product", width: 180 },
      { title: "سایز", dataIndex: "size_label", width: 80, render: (v) => v || "—" },
      ...(stage === "qc1"
        ? [{ title: "تعداد کنترل اول", dataIndex: "passed_qty", width: 130, align: "center",
            render: (v) => <b>{fmtNum(v)}</b> }]
        : [
            { title: "سالم", dataIndex: "passed_qty", width: 90, align: "center", render: fmtNum },
            { title: "زده‌دار", dataIndex: "rejected_qty", width: 90, align: "center", render: fmtNum },
            { title: "آمادهٔ ارسال", dataIndex: "final_qty", width: 120, align: "center",
              render: (v) => <b>{fmtNum(v)}</b> },
          ]),
      { title: "کنترل‌چی", dataIndex: "checker_name", width: 150, render: (v) => v || "—" },
      { title: "تاریخ", dataIndex: "check_date", width: 110, render: (v) => toJalali(v) },
      { title: "توضیح", dataIndex: "notes", width: 180, render: (v) => v || "—" },
      { title: "", width: 55,
        render: (_, q) => (
          <Popconfirm title="این کنترل حذف شود؟" okText="بله" cancelText="خیر"
            onConfirm={async () => {
              await deleteOrderQC(q.order_id, q.id); message.success("حذف شد"); refresh();
            }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ) },
    ];
    return <Table size="small" rowKey="id" dataSource={rows} columns={cols} pagination={false}
      scroll={{ x: "max-content" }} locale={{ emptyText: "ثبتی وجود ندارد" }} />;
  };

  const qc2Tab = (
    <>
      {qcEntryCard("qc2")}
      <Text type="secondary" style={{ fontSize: 12 }}>
        آمادهٔ ارسال = سالم + زده‌دار؛ همین عدد با تعداد بیجک برش مغایرت‌گیری می‌شود.
      </Text>
      {qcList("qc2")}
    </>
  );

  // ------------------------------------------------ تب ۲: عملیات و کارکرد + کنترل اول
  const filledDrafts = batchRows.filter((r) => r.catalog_id && r.employee_id && r.quantity > 0);
  const batchTotal = filledDrafts.reduce(
    (a, r) => a + (Number(r.total_rate) || 0) * (Number(r.quantity) || 0), 0);

  const draftCols = [
    {
      title: "عملیات", dataIndex: "operation_name", width: 220,
      render: (_, row) => (
        // تعدادِ خط داخلِ نامِ عملیات می‌آید و مبلغِ واحد در tooltip — تا ردیف کوتاه بماند
        <Tooltip title={money && row.catalog_id && !row.lines_missing
          ? `مبلغ هر واحد: ${rial(row.total_rate)}` : undefined}>
          <Select showSearch value={row.catalog_id} style={{ width: "100%" }}
            optionFilterProp="label" placeholder="عملیات"
            options={ratesOf(activeOrder)}
            onChange={(v) => updateDraft(row.rowKey, "catalog_id", v)}
            status={row.lines_missing ? "warning" : undefined} />
        </Tooltip>
      ),
    },
    {
      title: "سایز", dataIndex: "size_label", width: 110,
      render: (v, row) => (
        <Select allowClear value={v} style={{ width: "100%" }}
          options={sizeOptsOf(activeOrder)}
          onChange={(x) => updateDraft(row.rowKey, "size_label", x)} />
      ),
    },
    {
      title: "رنگ", dataIndex: "color", width: 140,
      render: (v, row) => (
        <CreatableSelect value={v} options={colorOptions} style={{ width: "100%" }}
          placeholder="رنگ (یا تایپِ رنگِ تازه)" onCreate={registerColor}
          onChange={(x) => updateDraft(row.rowKey, "color", x)} />
      ),
    },
    {
      title: "پرسنل", dataIndex: "employee_id", width: 180,
      render: (v, row) => (
        <Select showSearch allowClear value={v} style={{ width: "100%" }}
          optionFilterProp="label" placeholder="کارگر" options={empOpts}
          onChange={(x) => updateDraft(row.rowKey, "employee_id", x)} />
      ),
    },
    {
      title: "تعداد", dataIndex: "quantity", width: 110,
      render: (v, row) => (
        // مثلِ اکسل: «۱۲۰+۸۰+۴۵» و Enter ⇒ ۲۴۵ (چند نوبتِ کارِ یک نفر در یک ردیف)
        <FormulaNumber min={1} value={v} placeholder="مثلاً ۱۲۰+۸۰"
          onChange={(x) => updateDraft(row.rowKey, "quantity", x)} />
      ),
    },
    ...(!money ? [] : [{
      title: "فی واحد (ریال)", dataIndex: "total_rate", width: 130, align: "left",
      render: (v) => rialCell(v),
    },
    {
      title: "مبلغ (ریال)", width: 130, align: "left",
      render: (_, row) => (row.quantity > 0 && row.catalog_id
        ? <b className="gx-money">{formatMoney((row.total_rate || 0) * row.quantity)}</b>
        : "—"),
    }]),
    {
      title: "", width: 90,
      render: (_, row) => (
        <Space size={2}>
          <Tooltip title="کپی ردیف (بدون ذخیره تا ثبت)">
            <Button size="small" icon={<CopyOutlined />} onClick={() => duplicateDraft(row)} />
          </Tooltip>
          <Button size="small" danger icon={<DeleteOutlined />}
            onClick={() => removeDraft(row.rowKey)} />
        </Space>
      ),
    },
  ];

  const opsTab = (
    <>
      {/* 🔴 مهم‌ترین خطِ این تب: گریدِ ورود بلند است و ثبت‌های قبلی **زیرِ** آن
          می‌افتند. بدونِ این بنر، کاربر گرید را می‌دید و بی‌خبر دوباره ثبت
          می‌کرد. اینجا اولین چیزی است که چشم می‌بیند. */}
      {partState.entries > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`برای این پارت از قبل ${fmtNum(partState.entries)} ثبتِ عملیات وجود دارد`
            + ` — جمعاً ${fmtNum(partState.quantity)} عدد توسطِ ${fmtNum(partState.workers)} کارگر`
            + (partState.lastDate ? `، آخرین ثبت ${toJalali(partState.lastDate)}` : "")}
          action={<Button size="small" onClick={jumpToRecorded}>دیدنِ ثبت‌های قبلی</Button>}
          description="اگر همین کار را دوباره وارد کنید، دوباره حساب می‌شود. اول پایین را ببینید." />
      )}
      {partState.entries === 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="برای این پارت هنوز هیچ عملیاتی ثبت نشده — از گریدِ زیر شروع کنید." />
      )}

      {/* اطلاعاتِ پایهٔ همین صفحه — بالای گرید بماند */}
      {activePage && (
        <Descriptions size="small" bordered column={{ xs: 1, md: 4 }} style={{ marginBottom: 12 }}>
          <Descriptions.Item label="شماره بیجک">{data?.bijack_no || "—"}</Descriptions.Item>
          <Descriptions.Item label="کد تولید">{code}</Descriptions.Item>
          <Descriptions.Item label="محصول / مدل / رنگ / سایز">
            <b>{activePage.label}</b>
          </Descriptions.Item>
          <Descriptions.Item label="تعداد برش">{fmtNum(activePage.cut_qty)}</Descriptions.Item>
          {lastQc2 && (
            <Descriptions.Item label="آخرین کنترل نهایی">
              <Tag color="green">{toJalali(lastQc2)}</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}

      {/* v2.143.0 — وضعیتِ دستیِ این سایز: «دوخت تکمیل» (دوخت قفل؛ کنترل هنوز ممکن) / «تکمیل» (تمام) */}
      {activePage && (
        <Space size={6} wrap style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>وضعیتِ دستیِ این سایز:</Text>
          {activePage.manual_stage && <Tag color={STAGE_MAP[activePage.manual_stage]?.color}>
            {STAGE_MAP[activePage.manual_stage]?.label}</Tag>}
          {[["sewing_done", "دوخت تکمیل"], ["done", "تکمیل"]].filter(([v]) => v !== activePage.manual_stage).map(([v, l]) => (
            <Popconfirm key={v} title={`این سایز «${l}» بخورد؟`}
              description={v === "done" ? "کلِ تولیدِ این سایز تمام‌شده حساب می‌شود و دوختش قفل می‌شود."
                : "دوختِ این سایز تمام و قفل می‌شود؛ کنترل اول/نهایی هنوز ثبت می‌شود."}
              okText="بله" cancelText="خیر"
              onConfirm={() => setManualStage(activePage.order_id, activePage.size_label, v)
                .then(() => { message.success(`«${l}» ثبت شد`); refresh(); })
                .catch((e) => message.error(e?.response?.data?.detail || "ثبت نشد"))}>
              <Button size="small">{l}</Button>
            </Popconfirm>
          ))}
          {activePage.manual_stage && (
            <Button size="small" type="link" danger onClick={() => setManualStage(activePage.order_id, activePage.size_label, null)
              .then(() => { message.success("علامتِ دستی برداشته شد"); refresh(); })
              .catch((e) => message.error(e?.response?.data?.detail || "انجام نشد"))}>برداشتنِ علامت</Button>
          )}
        </Space>
      )}
      {activePage && (activePage.has_work !== false || activePage.sewing_status !== "new") && (
        <Space size={6} wrap style={{ marginBottom: 8 }}>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => resetSewingDialog(false)}>
            پاک‌کردنِ کلِ دوختِ این سایز
          </Button>
          <Button size="small" danger type="text" onClick={() => resetSewingDialog(true)}>
            کلِ دوختِ این محصول (همهٔ سایزها)
          </Button>
        </Space>
      )}

      {/* v2.142.0 — پیش‌نویسِ ذخیره‌نشده در سایزِ دیگرِ همین بیجک (مثلاً بعد از بروزرسانی صفحه روی سایزِ اول باز شد) */}
      {(() => {
        if (!code) return null;
        const others = listDrafts(`ops:${code}:`)
          .map((d) => ({ ...d, pageKey: d.key.slice(`ops:${code}:`.length) }))
          .filter((d) => d.pageKey !== String(activePageKey) && sewingPages.some((p) => String(p.key) === d.pageKey));
        if (!others.length) return null;
        return (
          <Alert type="warning" showIcon style={{ marginBottom: 8 }}
            message="برگهٔ ثبتِ کارکردِ ذخیره‌نشده در سایزِ دیگر"
            description={<Space wrap size={6}>
              {others.map((d) => {
                const pg = sewingPages.find((p) => String(p.key) === d.pageKey);
                return (
                  <Button key={d.key} size="small" type="primary" ghost onClick={() => setActivePageKey(pg.key)}>
                    برو به سایزِ {pg.size_label || pg.label || d.pageKey}
                  </Button>);
              })}
            </Space>} />
        );
      })()}
      <OpsGrid
        key={activePageKey}
        operations={ratesOf(activeOrder)}
        allOperations={allRateOps}
        productType={activeOrder?.product_type}
        onCreateOperation={createOperation}
        employees={empOpts}
        locked={activePageLocked}
        money={money}
        onSubmit={submitGrid}
        onUnlock={unlockActivePage}
        editRows={editGrid}
        onCancelEdit={closeEntryEdit}
        onDirtyChange={setOpsDirty}
        submitRef={opsSubmitRef}
        draftKey={code && activePageKey ? `ops:${code}:${activePageKey}` : null} />

      {qcEntryCard("qc1")}

      {/* فقط پارتِ همین صفحه — و در آن، فقط کارکردهای همین سایز.
          🐞 قبلاً روی همهٔ `orders` حلقه می‌زد و جمع‌بندیِ تجمیعیِ همهٔ سایزها را
          نشان می‌داد؛ در صفحهٔ 2XL ردیفِ «نوار رکاب XL» هم دیده می‌شد. */}
      {orders.filter((o) => o.order_id === activePage?.order_id).map((o) => {
        const size = activePage?.size_label || "";
        const ops = (o.operations_by_size || {})[size] || o.operations || {};
        const sizeEntries = (o.entries || []).filter(
          (e) => !activePage?.size_label || (e.size_label || null) === activePage.size_label);
        return (
          <Card key={o.order_id} size="small" style={{ marginBottom: 12 }}
            title={<><span ref={recordedRef} />{[o.product_type, o.color, activePage?.size_label]
              .filter(Boolean).join(" | ")}{" "}
              <Text type="secondary" style={{ fontSize: 12 }}>({o.order_number})</Text></>}
            extra={
              <Space wrap>
                <Text>محصول کامل تخمینی: <b>{fmtNum(ops.estimated_complete_qty)}</b></Text>
                <Text type="secondary">کمترین عملیات ثبت‌شده: {fmtNum(ops.min_operation_qty)}</Text>
                {money && <Text>کارمزد: <b>{rial(ops.total_amount)}</b></Text>}
              </Space>
            }>
            {!!ops.operations_with_diff?.length && (
              <Text type="warning" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                عملیات با تعداد بیشتر از کمترین: {ops.operations_with_diff.join("، ")} — حقوقشان
                کامل پرداخت می‌شود ولی در تعداد محصول کامل حساب نمی‌شود.
              </Text>
            )}
            <Table size="small" rowKey="operation_name" pagination={false}
              dataSource={ops.operations || []} scroll={{ x: "max-content" }}
              rowClassName={(r) => (r.warning ? "gx-op-warn" : "")}
              columns={[
                {
                  title: "عملیات", dataIndex: "operation_name", width: 220,
                  render: (name, r) => (
                    <Space size={4} wrap>
                      <span>{shortOpLabel(name, r.stitch_lines)}</span>
                      {r.warning && (
                        <Tooltip title={
                          r.lines_mismatch
                            ? `تعداد خط ثبت‌شده با نرخ‌نامه فعلی مغایرت دارد (نرخ‌نامه: ${fmtNum(r.catalog_stitch_lines)})`
                            : r.rate_mismatch
                              ? "نرخ ثبت‌شده با نرخ‌نامه فعلی متفاوت است — مبلغ قبلی Snapshot شده"
                              : "تعداد خط یا نرخ نیاز به بررسی دارد"
                        }>
                          <Tag color="gold">هشدار</Tag>
                        </Tooltip>
                      )}
                    </Space>
                  ),
                },
                { title: "خط", dataIndex: "stitch_lines", width: 70, render: (v) => fmtNum(v) },
                { title: "تعداد", dataIndex: "quantity", width: 110, align: "center",
                  render: (v, r) => {
                    // عملیاتِ جزئی که بخشی از کارش داخلِ عملیاتِ ترکیبی ثبت شده
                    // (زیگزال خشتکِ جدا + سهمش از «زیگزال تکمیلی قاپک و خشتک»)
                    if (r.merged_from?.length) {
                      return (
                        <Tooltip title={
                          `${fmtNum(v)} جدا + ` + r.merged_from.map((m) =>
                            `${fmtNum(m.quantity)} از «${m.operation_name}»`).join(" + ")}>
                          <b>{fmtNum(r.effective_qty)}</b>
                          <span style={{ fontSize: 10, opacity: 0.6 }}> (ادغام)</span>
                        </Tooltip>
                      );
                    }
                    return <b>{fmtNum(v)}</b>;
                  } },
                { title: "اختلاف با کمترین", dataIndex: "diff_vs_min", width: 130, align: "center",
                  render: (v, r) => (r.counted_in_parts
                    ? <Tooltip title="کارِ این عملیاتِ ترکیبی در جمعِ اجزایش حساب شده است">
                      <Tag>در جمعِ اجزا</Tag></Tooltip>
                    : (v ? <Tag color="orange">+{fmtNum(v)}</Tag> : <Tag color="green">۰</Tag>)) },
                ...(!money ? [] : [{ title: "مبلغ (ریال)", dataIndex: "amount", width: 130, align: "left",
                  render: (v) => rialCell(v) }]),
                { title: "کارگران", dataIndex: "workers", width: 260,
                  render: (ws) => (ws || []).map((w) => `${w.employee_name}: ${fmtNum(w.quantity)}`).join("، ") },
              ]} />
            {!!ops.operations_not_recorded?.length && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                عملیات نرخ‌نامه بدون ثبت (از محاسبهٔ محصول کامل خارج‌اند تا شروع شوند):{" "}
                {ops.operations_not_recorded.map((m) => shortOpLabel(m.operation_name, m.stitch_lines)).join("، ")}
              </Text>
            )}

            {/* ویرایش «عیناً همان برگهٔ ورود» — تا کنترل اول ثبت نشده */}
            <div style={{ margin: "8px 0" }}>
              <Tooltip title={activePageLocked
                ? "کنترل اول ثبت شده — برای ویرایش اول قفل را باز کنید" : ""}>
                <Button size="small" icon={<EditOutlined />} disabled={activePageLocked}
                  onClick={openEntryEdit}>
                  ویرایش در برگهٔ ورود (همان چیدمانِ ثبت)
                </Button>
              </Tooltip>
            </div>
            <EntriesTable
              entries={sizeEntries}
              money={money}
              layoutId={`bijack-entries-${o.product_type || ""}`}
              locked={activePageLocked}
              employees={empOpts}
              onEdit={(e) => setEditModal({ orderId: o.order_id, entry: { ...e } })}
              onSaveEntry={async (entryId, patch) => {
                await updateEntry(o.order_id, entryId, patch);
                message.success("ذخیره شد");
                await refresh();
              }}
              onBulk={async (list, patch) => {
                for (const e of list) await updateEntry(o.order_id, e.id, patch);
                message.success(`${fmtNum(list.length)} ثبت ویرایش شد`);
                await refresh();
              }}
              onDelete={async (e) => {
                await deleteEntry(o.order_id, e.id);
                await refresh();
              }} />
          </Card>
        );
      })}

      {/* ثبت‌های کنترل اولِ همین بیجک — کنارِ ثبت‌های عملیات، نه در تبِ جدا */}
      <Card size="small" title="ثبت‌های کنترل اول" style={{ marginTop: 12 }}>
        {qcList("qc1")}
      </Card>
    </>
  );


  // ------------------------------------------------ تب ۵/۶
  const [wQty, setWQty] = useState(null);
  const [wType, setWType] = useState("");
  const submitWaste = async () => {
    if (!wQty) { message.warning("تعداد را وارد کنید"); return false; }
    try {
      await createWasteEvent({ production_code: code, quantity: wQty, waste_type: wType || null });
      message.success("ضایعات ثبت شد"); setWQty(null); setWType(""); refresh();
      return true;
    } catch (e) {
      message.error(e?.response?.data?.detail || "ثبت ضایعات ناموفق بود");
      return false;
    }
  };
  const wasteTab = (
    <>
      <Card size="small" title="ثبت سریع ضایعات" style={{ marginBottom: 12 }}>
        <Space wrap align="end">
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>تعداد</Text>
            <div><InputNumber min={1} value={wQty} onChange={setWQty} style={{ width: 110 }} /></div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>نوع ضایعات</Text>
            <div><Input value={wType} onChange={(e) => setWType(e.target.value)} style={{ width: 150 }} /></div>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={submitWaste}>ثبت</Button>
        </Space>
      </Card>
      {/* فقط ضایعاتِ همین پارت/سایز — بقیهٔ صفحه‌ها ضایعاتِ خودشان را دارند */}
      <Table size="small" rowKey="id" pagination={false}
        dataSource={(data?.waste_events || []).filter((w) => (
          (!activePage?.order_id || w.order_id === activePage.order_id)
          && (!activePage?.size_label || (w.size_label || null) === activePage.size_label)))}
        scroll={{ x: "max-content" }} locale={{ emptyText: "ضایعاتی ثبت نشده" }}
        columns={[
          { title: "محصول", dataIndex: "product_type", width: 150 },
          { title: "سایز", dataIndex: "size_label", width: 80, render: (v) => v || "—" },
          { title: "نوع", dataIndex: "waste_type", width: 120, render: (v) => v || "—" },
          { title: "تعداد", dataIndex: "quantity", width: 80, align: "center", render: fmtNum },
          { title: "توضیح", dataIndex: "reason", width: 180, render: (v) => v || "—" },
          { title: "تاریخ", dataIndex: "event_date", width: 110, render: (v) => toJalali(v) },
          { title: "ثبت‌کننده", dataIndex: "recorded_by", width: 130, render: (v) => v || "—" },
          { title: "", width: 55,
            render: (_, w) => (
              <Popconfirm title="حذف شود؟" okText="بله" cancelText="خیر"
                onConfirm={async () => { await deleteWasteEvent(w.id); refresh(); }}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ) },
        ]} />
    </>
  );

  // سرور جمله را فارسی و به وقتِ تهران آماده می‌کند؛ اینجا فقط چیده می‌شود.
  const historyTab = (
    <GxTable size="small" rowKey="id" layoutId="bijack-history"
      dataSource={data?.history || []} pagination={{ pageSize: 20 }}
      scroll={{ x: "max-content" }} locale={{ emptyText: "تغییری ثبت نشده" }}
      columns={[
        { title: "تاریخ", dataIndex: "jalali_date", width: 110,
          sorter: (a, b) => String(a.created_at).localeCompare(String(b.created_at)) },
        { title: "ساعت", dataIndex: "clock", width: 80, align: "center" },
        { title: "کار", dataIndex: "action_fa", width: 130,
          render: (v, r) => <Tag color={ACTION_COLOR[r.action] || "default"}>{v}</Tag> },
        { title: "روی چه چیزی", dataIndex: "entity_fa", width: 130 },
        {
          title: "چه تغییری", dataIndex: "detail", width: 380,
          render: (v, r) => (r.changes?.length ? (
            <Space direction="vertical" size={0}>
              {r.changes.map((c) => (
                <span key={c.field} style={{ fontSize: 12 }}>
                  <b>{c.label}:</b>{" "}
                  <Text delete type="secondary">{c.from}</Text>{" ← "}
                  <Text strong>{c.to}</Text>
                </span>
              ))}
            </Space>
          ) : (v || <Text type="secondary">—</Text>)),
        },
        { title: "کاربر", dataIndex: "actor", width: 120 },
        { title: "یادداشت", dataIndex: "note", width: 200, render: (v) => v || "—" },
      ]} />
  );

  /**
   * جعبهٔ «وضعیتِ این پارت» — عمداً **جدا از عنوانِ صفحه**.
   *
   * قبلاً انتخاب‌گرِ پارت و «ماه محاسبه» کنارِ عنوان و دکمه‌های ویرایش/تازه‌سازی
   * چپانده شده بودند؛ معلوم نبود کدام مربوط به کلِ بیجک است و کدام مربوط به
   * همین پارت. حالا هرچه **مالِ همین پارت** است داخلِ یک کادر است: مرحله‌اش
   * (آماده دوخت / در حال دوخت / کنترل اول / کنترل نهایی)، ماهِ حقوقی‌اش، و
   * آنچه از قبل زیرش ثبت شده.
   */
  const stageInfo = STAGE_MAP[activePage?.sewing_status] || null;
  const partStateCard = !activePage ? null : (
    <Card size="small" style={{ marginBottom: 12 }}
      styles={{ body: { paddingBlock: 10 } }}
      title={
        <Space wrap size={8}>
          <span>وضعیتِ این پارت</span>
          {stageInfo && <Tag color={stageInfo.color} style={{ margin: 0 }}>{stageInfo.label}</Tag>}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {stageInfo?.hint}
          </Text>
        </Space>
      }>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Space wrap size={10}>
          <Text type="secondary" style={{ fontSize: 12 }}>پارتِ در دستِ کار:</Text>
          <b>{activePage.label}</b>
          {activePage.orphan && (
            <Tooltip title="این سایز در بیجکِ برش نیست ولی دوخت/کنترل دارد (معمولاً بعد از ثبتِ مجددِ بیجک با سایزِ دیگر). با افزودنِ سایز به بیجک، اعدادِ برش و مقایسه‌ها درست می‌شوند.">
              <Button size="small" danger icon={<PlusOutlined />} onClick={addOrphanSizeToBijack}>
                بدونِ برش — افزودن به بیجک
              </Button>
            </Tooltip>
          )}
          {sewingPages.length > 1 && (
            <Select size="small" style={{ minWidth: 330 }} value={activePageKey}
              onChange={setActivePageKey} optionLabelProp="label"
              options={sewingPages.map((pg) => ({
                value: pg.key,
                label: pg.label,
                title: pg.label,
                children: (
                  <Space size={4}>
                    <Tag color={STAGE_MAP[pg.sewing_status]?.color} style={{ margin: 0 }}>
                      {STAGE_MAP[pg.sewing_status]?.label}
                    </Tag>
                    <span>{pg.label}</span>
                    {pg.orphan
                      ? <Tag color="red" style={{ margin: 0 }}>بدونِ برش</Tag>
                      : <Text type="secondary" style={{ fontSize: 11 }}>برش {fmtNum(pg.cut_qty)}</Text>}
                  </Space>
                ),
              }))}
              optionRender={(o) => o.data.children} />
          )}
          {/* ماهِ محاسبهٔ حقوقِ این پارت — «این پارت برای تیر است».
              کارِ پارت ممکن است خرداد باشد ولی حقوقش تیر بنشیند. */}
          <Space size={4}>
            <span style={{ fontSize: 12, color: "var(--text-sec)" }}>ماه محاسبه:</span>
            {/* v2.163.1: قفل فقط وقتی این سایز کارِ ثبت‌شده دارد — پارتِ خالیِ با ماهِ اشتباهِ بسته (۷۲۹) آزاد است */}
            <Tooltip title={closedMonths.has(payrollMonth) && activePage?.has_work !== false
              ? "حقوقِ این ماه بسته شده — ماهِ محاسبه و دوختِ این پارت قفل است (کنترلِ نهایی آزاد است)"
              : closedMonths.has(payrollMonth) ? "ماهِ بسته ولی هنوز کاری ندارد — می‌شود ماه را برداشت یا عوض کرد" : ""}>
              <Select size="small" style={{ minWidth: 130 }} value={payrollMonth} allowClear
                placeholder="طبقِ تاریخِ کار" options={monthOpts}
                disabled={closedMonths.has(payrollMonth) && activePage?.has_work !== false}
                suffixIcon={closedMonths.has(payrollMonth) && activePage?.has_work !== false ? "🔒" : undefined}
                onChange={savePayrollMonth} loading={savingMonth} />
            </Tooltip>
            <HelpPopover label="" title="ماه محاسبهٔ حقوقِ این پارت" content={
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <b>این تعیین می‌کند کارکردِ این پارت در حقوقِ کدام ماه بنشیند.</b>
                <ul style={{ paddingInlineStart: 18, margin: "6px 0" }}>
                  <li><b>خالی</b> = طبقِ تاریخِ کار (پیش‌فرض). کارِ ۱۸ مرداد در حقوقِ <b>مرداد</b> می‌آید.</li>
                  <li><b>انتخابِ ماه</b> = هر چه زیرِ این پارت ثبت شود — برش، دوخت، کنترل نهایی —
                    در حقوقِ همان ماه حساب می‌شود، حتی اگر تاریخِ کارش ماهِ دیگری باشد.</li>
                </ul>
                <b>اگر کارکردی در محاسبهٔ حقوق دیده نمی‌شود</b>، اول همین‌جا را نگاه کنید:
                معمولاً یا ماهِ محاسبه خالی است و تاریخِ کار در ماهِ دیگری افتاده،
                یا ماهِ اشتباهی انتخاب شده. با عوض‌کردنِ این گزینه، همهٔ ثبت‌های
                موجودِ این پارت هم بلافاصله جابه‌جا می‌شوند.
                <div style={{ marginTop: 6 }}>
                  یادآوری: فقط پرسنلی که مدلِ حقوقشان <b>کارمزد</b> است از این مسیر پول می‌گیرند.
                </div>
              </div>
            } />
          </Space>
        </Space>

        {/* ── چه چیزی از قبل ثبت شده ──
            این تنها جایی است که بدونِ اسکرول معلوم می‌کند کارِ این پارت قبلاً
            وارد شده یا نه. هر تگ کلیک‌شدنی است و می‌برد سرِ همان بخش. */}
        <Space wrap size={6} align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>از قبل ثبت شده:</Text>
          {partState.entries ? (
            <Tag color="orange" style={{ cursor: "pointer" }} onClick={jumpToRecorded}>
              عملیات: {fmtNum(partState.entries)} ثبت — {fmtNum(partState.quantity)} عدد،
              {" "}{fmtNum(partState.workers)} کارگر، {fmtNum(partState.ops)} عملیات
            </Tag>
          ) : <Tag>عملیات: هنوز هیچ</Tag>}
          <Tag color={partState.qc1 ? "purple" : undefined}
            style={{ cursor: "pointer" }} onClick={() => setTab("ops")}>
            کنترل اول: {partState.qc1 ? `${fmtNum(partState.qc1)} ثبت` : "—"}
          </Tag>
          <Tag color={partState.qc2 ? "green" : undefined}
            style={{ cursor: "pointer" }} onClick={() => setTab("qc2")}>
            کنترل نهایی: {partState.qc2 ? `${fmtNum(partState.qc2)} ثبت` : "—"}
          </Tag>
          <Tag color={partState.waste ? "red" : undefined}
            style={{ cursor: "pointer" }} onClick={() => setTab("waste")}>
            ضایعات: {partState.waste ? `${fmtNum(partState.waste)} ثبت` : "—"}
          </Tag>
          {partState.lastDate && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              آخرین ثبت: {toJalali(partState.lastDate)}
              {partState.firstDate !== partState.lastDate
                && <> (از {toJalali(partState.firstDate)})</>}
            </Text>
          )}
          {activePageLocked && <Tag color="red">دوخت قفل است — کنترل ثبت شده</Tag>}
        </Space>
      </Space>
    </Card>
  );

  /* ── محافظتِ خروج (خواستهٔ کاربر ۱۴۰۵/۰۷/۰۱) ──
     «ممکنه دستش بخوره قسمتِ خالیِ صفحه و کلاً بسته بشه… یه پاپ‌آپ بیاد که مطمئن هستید؟
     انصراف داشته باشه، ذخیره داشته باشه». کلیک روی فضای خالی/Esc همیشه می‌پرسد؛ دکمهٔ ×
     فقط وقتی چیزی ذخیره‌نشده مانده. «ذخیره و خروج» همهٔ پیش‌نویس‌ها را ثبت می‌کند و فقط
     اگر همه موفق بودند می‌بندد. */
  const pending = [
    opsDirty && (editGrid ? "ویرایشِ ثبت‌های عملیات" : "برگهٔ ثبتِ عملیات و کارکرد"),
    qcFilled("qc1").length > 0 && "کنترل اول",
    qcFilled("qc2").length > 0 && "کنترل نهایی",
    !!wQty && "ضایعات",
  ].filter(Boolean);
  const requestClose = (e) => {
    const accidental = !e || e.type === "keydown"
      || (e.target && e.target.classList && e.target.classList.contains("ant-drawer-mask"));
    if (!pending.length && !accidental) { onClose(); return; }
    setLeaveAsk(true);
  };
  const saveAndClose = async () => {
    setLeaveSaving(true);
    let ok = true;
    if (opsDirty && opsSubmitRef.current) ok = (await opsSubmitRef.current()) && ok;
    if (ok && qcFilled("qc1").length) ok = (await submitQCBatch("qc1")) && ok;
    if (ok && qcFilled("qc2").length) ok = (await submitQCBatch("qc2")) && ok;
    if (ok && wQty) ok = (await submitWaste()) && ok;
    setLeaveSaving(false);
    if (ok) { setLeaveAsk(false); onClose(); }
  };

  const items = [
    { key: "compare", label: "مقایسه و مغایرت", children: compareTab },
    // کنترل اول داخلِ همین تب است — همان اطلاعاتِ عملیات و کارکرد مبنایش است
    { key: "ops", label: "عملیات و کارکرد / کنترل اول", children: opsTab },
    { key: "qc2", label: "کنترل نهایی", children: qc2Tab },
    { key: "waste", label: "ضایعات", children: wasteTab },
    { key: "history", label: "تاریخچهٔ تغییرات", children: historyTab },
  ];

  return (
    <Drawer open={open} onClose={requestClose} width="92%" destroyOnClose
      title={
        <Space wrap>
          <Title level={5} style={{ margin: 0 }}>
            {data?.product_label || `کد تولید ${code}`}
          </Title>
          <Tag color="blue">کد تولید {code}</Tag>
          {data?.bijack_no && <Tag>شماره بیجک {data.bijack_no}</Tag>}
          {/* عنوان فقط هویتِ **کلِ بیجک** را دارد. هرچه مالِ یک پارتِ خاص است
             (انتخابِ پارت، مرحله، ماهِ محاسبه) رفت داخلِ کارتِ «وضعیتِ این
             پارت» زیرِ همین هدر — قبلاً همه با هم اینجا بودند و معلوم نبود
             کدام به کلِ بیجک مربوط است و کدام به یک سایز. */}
          {/* ویرایش/حذفِ خودِ بیجک همان‌جایی انجام می‌شود که ثبت شده — «ثبت برش» */}
          <Tooltip title="ویرایش یا حذفِ این بیجک در صفحهٔ «ثبت برش»">
            <Button size="small" icon={<EditOutlined />}
              onClick={() => window.open(`/production/cut?edit=${encodeURIComponent(code)}`, "_self")}>
              ویرایش برش
            </Button>
          </Tooltip>
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>
        </Space>
      }>
      {!data ? <Empty description={loading ? "در حال بارگذاری…" : "اطلاعاتی نیست"} /> : (
        <>
          <Row gutter={[10, 10]} style={{ marginBottom: 12 }}
            key={`stats-${data?.scoped_order_id || code}`}>
            <Col xs={12} md={4}><Card size="small"><Statistic title="بیجک برش" value={summary.cut_qty} /></Card></Col>
            <Col xs={12} md={4}><Card size="small"><Statistic title="کنترل اول" value={summary.qc1_qty} /></Card></Col>
            <Col xs={12} md={4}><Card size="small"><Statistic title="سالم" value={summary.good_qty} /></Card></Col>
            <Col xs={12} md={4}><Card size="small"><Statistic title="زده‌دار" value={summary.defect_qty} /></Card></Col>
            <Col xs={12} md={4}><Card size="small"><Statistic title="کنترل نهایی" value={summary.final_qty} /></Card></Col>
            <Col xs={12} md={4}>
              <Card size="small">
                <Statistic title="محصول کامل تخمینی" value={summary.estimated_complete_qty} />
              </Card>
            </Col>
          </Row>

          <Descriptions size="small" bordered column={{ xs: 1, md: 3 }} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="تاریخ برش">{toJalali(data.entry_date)}</Descriptions.Item>
            <Descriptions.Item label="برشکار">{data.cutter_name || "—"}</Descriptions.Item>
            {money && <Descriptions.Item label="حقوق برشکار">{rial(data.cutter_amount)}</Descriptions.Item>}
            {money && <Descriptions.Item label="کارمزد دوخت">{rial(data.sewing_amount)}</Descriptions.Item>}
            <Descriptions.Item label="جمع ضایعات">{fmtNum(data.waste_total)}</Descriptions.Item>
            <Descriptions.Item label="یادداشت">{data.notes || "—"}</Descriptions.Item>
          </Descriptions>

          {partStateCard}

          <GxTabs layoutId="bijack-drawer" items={items}
            activeKey={tab} onChange={setTab} />
        </>
      )}

      <Modal open={leaveAsk} centered width={440} closable={false} maskClosable={false}
        onCancel={() => setLeaveAsk(false)}
        title={<Space><ExclamationCircleFilled style={{ color: pending.length ? "#faad14" : "#1677ff" }} />
          {pending.length ? "تغییرات ذخیره نشده‌اند" : "از صفحهٔ این محصول خارج می‌شوید؟"}</Space>}
        footer={[
          <Button key="stay" onClick={() => setLeaveAsk(false)}>انصراف (ماندن)</Button>,
          <Button key="leave" danger={pending.length > 0}
            onClick={() => { setLeaveAsk(false); onClose(); }}>
            {pending.length ? "خروج بدونِ ذخیره" : "خروج"}
          </Button>,
          ...(pending.length ? [
            <Button key="save" type="primary" icon={<SaveOutlined />} loading={leaveSaving}
              onClick={saveAndClose}>ذخیره و خروج</Button>] : []),
        ]}>
        {pending.length ? (
          <>
            <div style={{ marginBottom: 6 }}>این بخش‌ها هنوز ثبت نشده‌اند:</div>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {pending.map((p) => <li key={p}><b>{p}</b></li>)}
            </ul>
            <div style={{ marginTop: 8, color: "#888", fontSize: 12 }}>
              با «خروج بدونِ ذخیره» همین پیش‌نویس‌ها پاک می‌شوند.
            </div>
          </>
        ) : (
          <div style={{ color: "#888" }}>چیزی ذخیره‌نشده نمانده؛ فقط برای اطمینان پرسیدیم.</div>
        )}
      </Modal>

      <Modal open={!!editModal} title="ویرایش ثبت عملیات" onOk={saveEdit}
        onCancel={() => setEditModal(null)} okText="ذخیره" cancelText="انصراف" destroyOnClose>
        {editModal && (
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>پرسنل</Text>
              <Select showSearch value={editModal.entry.employee_id} options={empOpts}
                optionFilterProp="label" style={{ width: "100%" }}
                onChange={(v) => setEditModal((m) => ({ ...m, entry: { ...m.entry, employee_id: v } }))} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>عملیات</Text>
              <Select showSearch value={editModal.entry.catalog_id}
                options={rates.map((c) => ({
                  value: c.id, label: `${c.product_type} — ${shortOpLabel(c.operation_name, c.stitch_lines)}`,
                }))}
                optionFilterProp="label" style={{ width: "100%" }}
                onChange={(v) => setEditModal((m) => ({ ...m, entry: { ...m.entry, catalog_id: v } }))} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>تعداد</Text>
              <FormulaNumber min={1} value={editModal.entry.quantity}
                onChange={(v) => setEditModal((m) => ({ ...m, entry: { ...m.entry, quantity: v } }))} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>تاریخ</Text>
              <JalaliDateInput value={editModal.entry.entry_date}
                onChange={(v) => setEditModal((m) => ({ ...m, entry: { ...m.entry, entry_date: v } }))} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>توضیحات</Text>
              <Input.TextArea rows={2} value={editModal.entry.notes || ""}
                onChange={(e) => setEditModal((m) => ({
                  ...m, entry: { ...m.entry, notes: e.target.value },
                }))} />
            </div>
          </Space>
        )}
      </Modal>
    </Drawer>
  );
}
